import api from './api'
import { PageResponse } from '@/types/comum'
import {
  ChecklistReserva,
  DefesaRastreabilidade,
  ReservaEstruturacao,
} from '@/types/estruturacao'

const BASE_URL = '/api/v1/oportunidades'

/**
 * Workspace de estruturação do consultor (Onda 4 / A09): endpoints do
 * `EstruturacaoConsultorController` (A08). Service PRÓPRIO — o `oportunidadeService`
 * pertence a outra frente e não ganha métodos novos aqui.
 *
 * Escopo: o backend aplica linha a linha (CONSULTOR só alcança reserva do próprio
 * tenant E da própria carteira; back-office é global). Reserva fora do escopo volta
 * como "Oportunidade não encontrada" (fail-closed) — ver `ehSemPermissao`.
 */
export const estruturacaoConsultorService = {
  /**
   * Reservas EM ESTRUTURAÇÃO da carteira — filtro no SERVIDOR pela etapa comercial
   * DERIVADA (`?etapaComercial=ESTRUTURACAO`, `OportunidadeController.listarOportunidades`
   * → `filtrarPorEtapaComercial`, já escopado por tenant + consultor). A tela não filtra
   * no cliente: a resposta É a lista, e os KPIs são agregação de apresentação dela.
   *
   * Desde a Onda 6 (A11) o filtro é pela `EtapaComercial`, não pelo estágio legado:
   * a devolução do parceiro (`REPROVADA_PARCEIRO`, estágio legado NEGOCIACAO) ENTRA na
   * fila, como a régua do Figma prevê. Lacuna (b) da Rodada 2 fechada.
   */
  listarEmEstruturacao: async (
    page = 0,
    size = 200
  ): Promise<PageResponse<ReservaEstruturacao>> => {
    return (await api.get<PageResponse<ReservaEstruturacao>>(
      `${BASE_URL}?etapaComercial=ESTRUTURACAO&page=${page}&size=${size}`
    )) as unknown as PageResponse<ReservaEstruturacao>
  },

  /** A reserva completa (GET do detalhe), com os campos da Onda 4 tipados. */
  reserva: async (id: string): Promise<ReservaEstruturacao> => {
    return (await api.get<ReservaEstruturacao>(`${BASE_URL}/${id}`)) as unknown as ReservaEstruturacao
  },

  // O PATCH `/{id}/dados-complementares` (bloco fixo V39) saiu com L6: os dados que o
  // produto pede vêm do formulário dinâmico (`formularioService`).

  /** Checklist documental agrupado por categoria + percentual do header. */
  checklist: async (id: string): Promise<ChecklistReserva> => {
    return (await api.get<ChecklistReserva>(
      `${BASE_URL}/${id}/checklist`
    )) as unknown as ChecklistReserva
  },

  /** Linha do tempo derivada da Defesa de Crédito — NUNCA inclui a minuta. */
  rastreabilidade: async (id: string): Promise<DefesaRastreabilidade> => {
    return (await api.get<DefesaRastreabilidade>(
      `${BASE_URL}/${id}/defesa/rastreabilidade`
    )) as unknown as DefesaRastreabilidade
  },
}

/**
 * Reserva de outro consultor (ou inexistente) volta do backend como 400
 * `INVALID_ARGUMENT` com a mensagem "Oportunidade não encontrada" — deliberadamente
 * indistinguíveis (fail-closed). A tela trata os dois como "sem permissão"; 403 idem.
 * Desde a P3 o checklist (e os endpoints de `/{id}/checklist/**`) devolvem 404 fora do escopo.
 */
export const ehSemPermissao = (e: unknown): boolean => {
  const erro = e as { status?: number; message?: string }
  if (erro?.status === 403 || erro?.status === 404) return true
  return (erro?.message ?? '').toLowerCase().includes('não encontrada')
}
